import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { ApplicationStatusesInitDataDTO, EmailCommand, LookupItemDTO } from '../../shared/api-common-models';
import { ReviewApplicationInitDTO, SaveStepFeedbackPermissionsDTO, StepFeedbackPermissionsInitDTO, ApplicationFeedbackType } from './models/api-models';
import { SaveCommentCommand } from './comments/comments.service';

@Injectable({
  providedIn: 'root'
})
export class RecruitmentService {
  private _onApplicationStatusChangedSubject: Subject<ApplicationStatusEventChanged | null> = new Subject<ApplicationStatusEventChanged | null>();
  public onApplicationStatusChanged$: Observable<ApplicationStatusEventChanged | null> = this._onApplicationStatusChangedSubject.asObservable();

  constructor(@Inject('API_URL') private _apiUrl: string, private _http: HttpClient) { }

  public getReviewApplicationInitData(applicationId: number): Observable<ReviewApplicationInitDTO> {
    let url: string = `${this._apiUrl}/admin/applications/${applicationId}/review-init`;

    return this._http.get<ReviewApplicationInitDTO>(url).pipe(
      tap((res: ReviewApplicationInitDTO) => {
        if (res.isFirstReview) {
          this.updateCandidateStatus(null);
        }
      })
    );
  }

  public getApplicationStatusesInitData(applicationId: number, isRejection: boolean): Observable<ApplicationStatusesInitDataDTO<any>> {
    let url: string = `${this._apiUrl}/admin/applications/statuses-init?applicationId=${applicationId}&getOnlyRejectionStatuses=${isRejection}`;

    return this._http.get<ApplicationStatusesInitDataDTO<any>>(url);
  }

  public setApplicationStatus(jobApplicationId: number, command: SaveApplicationStatusCDTO): Observable<UpdateApplicationStatusDTO> {
    let url: string = `${this._apiUrl}/admin/applications/${jobApplicationId}/status`;

    return this._http.post<UpdateApplicationStatusDTO>(url, command).pipe(
      tap((res: UpdateApplicationStatusDTO) => {
        // notify all components displaying the status that the value has been changed
        this.updateCandidateStatus({ candidateStatusName: res.candidateStatusName, candidateIconUrl: res.candidateIconUrl });
      })
    );
  }

  public rejectApplication(jobApplicationId: number, command: SaveApplicationStatusCDTO): Observable<number> {
    let url: string = `${this._apiUrl}/admin/applications/${jobApplicationId}/status`;

    return this._http.post<number>(url, command);
  }

  // start step
  public startStep(jobApplicationId: number, stepId: number): Observable<number> {
    let url: string = `${this._apiUrl}/admin/applications/${jobApplicationId}/steps/${stepId}/start`;

    return this._http.post<number>(url, null);
  }

  // close step
  public completeStep(jobApplicationId: number, stepId: number): Observable<number> {
    let url: string = `${this._apiUrl}/admin/applications/${jobApplicationId}/steps/${stepId}/complete`;

    return this._http.post<number>(url, null);
  }

  // get all users
  public getSABISCareerUsers(): Observable<LookupItemDTO<any>[]> {
    let url: string = `${this._apiUrl}/admin/candidates/get-all-users`;

    return this._http.get<LookupItemDTO<any>[]>(url);
  }

  // get the already set `selection step feedback permissions` (allowedTo/ restrictedFrom)
  public getStepFeedbackPermissionsInitData(applicationId: number, selectionStepId: number, applicationFeedbackType: ApplicationFeedbackType): Observable<StepFeedbackPermissionsInitDTO> {
    let url: string = `${this._apiUrl}/admin/applications/${applicationId}/steps/${selectionStepId}/feedback/permissions/editor?applicationFeedbackType=${applicationFeedbackType}`;

    return this._http.get<StepFeedbackPermissionsInitDTO>(url);
  }


  public saveFeedbackPermission(command: SaveStepFeedbackPermissionsDTO): Observable<number> {
    let url: string = `${this._apiUrl}/admin/applications/steps/feedback/permissions`;
    return this._http.post<number>(url, command);
  }

  public updateStepFeedbackPermission(selectionStepFeedbackPermissionId: number, command: SaveStepFeedbackPermissionsDTO): Observable<number> {
    let url: string = `${this._apiUrl}/admin/candidates/applications/selectionStepFeedbackPermissions/${selectionStepFeedbackPermissionId}`;

    return this._http.post<number>(url, command);
  }

  /**
   *
   * @param jobApplicationId
   * @param stepId
   * @returns
   */
  public withdrawFromStep(jobApplicationId: number, stepId: number): Observable<number> {
    let url: string = `${this._apiUrl}/admin/applications/${jobApplicationId}/steps/${stepId}/withdraw`;

    return this._http.post<number>(url, null);
  }

  public updateCandidateStatus(statusEvent: ApplicationStatusEventChanged | null): void {
    this._onApplicationStatusChangedSubject.next(statusEvent);
  }
}

export interface SaveApplicationStatusCDTO {
  email: EmailCommand | null,
  jobApplicationStatusId: number

  selectionStepId: number | null;
  saveCommentCommand: SaveCommentCommand | undefined;
  applicationStatusReasonId: number | null;
}

export interface ApplicationStatusDTO {
  applicationStatusId: number;
  name: string;
  candidateStatusId: number
}

export interface UpdateApplicationStatusDTO {
  jobApplicationId: number;
  candidateStatusName: string;
  candidateIconUrl: string;
}

export interface ApplicationStatusEventChanged {
  candidateStatusName: string;
  candidateIconUrl: string;
}
